import AbstractInstaller from "../core/application/AbstractInstaller";
import SkinKeyRegistry from "../core/ui/SkinKeyRegistry";
import ScenarioPlayerEvent from "../ngt/scenario/ScenarioPlayerEvent";
import BriefingCommand from "./briefing/controller/BriefingCommand";
import BriefingScreenMediator from "./briefing/view/BriefingScreenMediator";
import PlayConversationStatementCommand from "./conversation-statements/command/PlayConversationStatementCommand";
import ConversationStatementScreenMediator from "./conversation-statements/view/ConversationStatementScreenMediator";
import HubKeyDecisionListItemFactory from "./hub/controller/HubKeyDecisionListItemFactory";
import HubKeyDecisionListItemMediator from "./hub/view/HubKeyDecisionListItemMediator";
import HubKeyDecisionListMediator from "./hub/view/HubKeyDecisionListMediator";
import HubScreenMediator from "./hub/view/HubScreenMediator";
import InternalStakeholderAvailabilityCommand from "./internal-stakeholders/controller/InternalStakeholderAvailabilityCommand";
import RequestConversationInternalStakeholderCommand from "./internal-stakeholders/controller/RequestConversationInternalStakeholderCommand";
import InternalStakeholderAvailabilityEvent from "./internal-stakeholders/event/InternalStakeholderAvailabilityEvent";
import InternalStakeholderInteractionEvent from "./internal-stakeholders/event/InternalStakeholderInteractionEvent";
import InternalStakeholdersModel from "./internal-stakeholders/model/InternalStakeholdersModel";
import InternalStakeholdersConfig from "./internal-stakeholders/model/InternalStakeholdersConfig";
import InitInternalStakeholdersTask from "./internal-stakeholders/task/InitInternalStakeholdersTask";
import InternalStakeholdersMediator from "./internal-stakeholders/view/InternalStakeholdersMediator";
import NotifyKeyDecisionAvailableCommand from "./key-decisions/controller/NotifyKeyDecisionAvailableCommand";
import KeyDecisionCompletedCommand from "./key-decisions/controller/KeyDecisionCompletedCommand";
import KeyDecisionContentAvailableCommand from "./key-decisions/controller/KeyDecisionContentAvailableCommand";
import KeyDecisionContentCompletedCommand from "./key-decisions/controller/KeyDecisionContentCompletedCommand";
import KeyDecisionContentUnavailableCommand from "./key-decisions/controller/KeyDecisionContentUnavailableCommand";
import RequestKeyDecisionCommand from "./key-decisions/controller/RequestKeyDecisionCommand";
import KeyDecisionContentEvent from "./key-decisions/event/KeyDecisionContentEvent";
import KeyDecisionEvent from "./key-decisions/event/KeyDecisionEvent";
import KeyDecisionInteractionEvent from "./key-decisions/event/KeyDecisionInteractionEvent";
import KeyDecisionsConfig from "./key-decisions/model/KeyDecisionsConfig";
import KeyDecisionsModel from "./key-decisions/model/KeyDecisionsModel";
import InitKeyDecisionsConfigTask from "./key-decisions/task/InitKeyDecisionsConfigTask";
import KeyDecisionAvailableScreenMediator from "./key-decisions/view/KeyDecisionAvailableScreenMediator";
import KeyDecisionOverlayScreenMediator from "./key-decisions/view/KeyDecisionOverlayScreenMediator";
import KeyDecisionUnderlayScreenMediator from "./key-decisions/view/KeyDecisionUnderlayScreenMediator";
import MapCharacterInteractionCommand from "./map/controller/MapCharacterInteractionCommand";
import MapFactory from "./map/controller/MapFactory";
import MapMutationCommand from "./map/controller/MapMutationCommand";
import MapCharacterInteractionEvent from "./map/event/MapCharacterInteractionEvent";
import MapModel from "./map/model/MapModel";
import MapsConfig from "./map/model/MapsConfig";
import InitMapsConfigTask from "./map/task/InitMapsConfigTask";
import MapCharacterInformationPopupMediator from "./map/view/MapCharacterInformationPopupMediator";
import MapLocationCharacterMenuMediator from "./map/view/MapLocationCharacterMenuMediator";
import MapLocationMediator from "./map/view/MapLocationMediator";
import MapMediator from "./map/view/MapMediator";
import PlaceholderCommand from "./placeholder/controller/PlaceholderCommand";
import PlaceholderScreenMediator from "./placeholder/view/PlaceholderScreenMediator";
import TransitionCommand from "./transition/controller/TransitionCommand";
import TransitionScreenMediator from "./transition/view/TransitionScreenMediator";
import InternalStakeholderContentCommand from "./internal-stakeholders/controller/InternalStakeholderContentCommand";
import TeaCornerContentAvailableCommand from "./tea-corner/controller/TeaCornerContentAvailableCommand";
import TeaCornerContentUnavailableCommand from "./tea-corner/controller/TeaCornerContentUnavailableCommand";
import TeaCornerModel from "./tea-corner/model/TeaCornerModel";
import TeaCornerOpenerMediator from "./tea-corner/view/TeaCornerOpenerMediator";

export default class AppSpecificInstaller extends AbstractInstaller {
    /**
     * @param {infuse.Injector} injector 
     * @param {soma.Commands} commands 
     * @param {MediatorMapping} mediatorMap 
     */
    constructor(injector, commands, mediatorMap) {
        super(injector, commands, mediatorMap);

        commands.add(ScenarioPlayerEvent.TEXT_AVAILABLE, PlaceholderCommand);
        commands.add(ScenarioPlayerEvent.UNLOCK_LOCATION_AVAILABLE, BriefingCommand);
        commands.add(ScenarioPlayerEvent.MAP_MUTATION_AVAILABLE, MapMutationCommand);
        commands.add(ScenarioPlayerEvent.PUSH_CONVERSATION_STATEMENT_AVAILABLE, PlayConversationStatementCommand);
        commands.add(ScenarioPlayerEvent.NARRATIVE_EVENT_AVAILABLE, TransitionCommand);
        commands.add(ScenarioPlayerEvent.CHANGE_ENTITY_VISIBILITY_AVAILABLE, InternalStakeholderAvailabilityCommand);
        commands.add(ScenarioPlayerEvent.INTERNAL_STAKEHOLDER_CONTENT_AVAILABLE, InternalStakeholderContentCommand);
        commands.add(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_AVAILABLE, TeaCornerContentAvailableCommand);
        commands.add(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_UNAVAILABLE, TeaCornerContentUnavailableCommand);

        commands.add(MapCharacterInteractionEvent.CLICK, MapCharacterInteractionCommand);
        commands.add(MapCharacterInteractionEvent.ENTER, MapCharacterInteractionCommand);

        commands.add(KeyDecisionContentEvent.KEY_DECISION_CONTENT_AVAILABLE, KeyDecisionContentAvailableCommand);
        commands.add(KeyDecisionContentEvent.KEY_DECISION_CONTENT_UNAVAILABLE, KeyDecisionContentUnavailableCommand);
        commands.add(KeyDecisionContentEvent.KEY_DECISION_CONTENT_COMPLETED, KeyDecisionContentCompletedCommand);
        commands.add(KeyDecisionEvent.KEY_DECISION_COMPLETED, KeyDecisionCompletedCommand);
        commands.add(KeyDecisionInteractionEvent.REQUEST_KEY_DECISION, RequestKeyDecisionCommand);
        commands.add(KeyDecisionInteractionEvent.NOTIFY_KEY_DECISION_AVAILABLE, NotifyKeyDecisionAvailableCommand);

        commands.add(InternalStakeholderInteractionEvent.REQUEST_CONVERSATION, RequestConversationInternalStakeholderCommand);

        injector.mapClass("mapFactory", MapFactory, true);
        injector.mapClass("mapsConfig", MapsConfig, true);
        injector.mapClass("mapModel", MapModel, true);
        injector.mapClass("keyDecisionsConfig", KeyDecisionsConfig, true);
        injector.mapClass("keyDecisionsModel", KeyDecisionsModel, true);
        injector.mapClass("hubKeyDecisionListItemFactory", HubKeyDecisionListItemFactory, true);
        injector.mapClass("internalStakeholdersConfig", InternalStakeholdersConfig, true);
        injector.mapClass("internalStakeholdersModel", InternalStakeholdersModel, true);
        injector.mapClass("teaCornerModel", TeaCornerModel, true);

        SkinKeyRegistry.PLACEHOLDER_SCREEN_SKIN = this._mapMediator(PlaceholderScreenMediator, lib.MainAssets.PlaceholderScreenSkin);
        SkinKeyRegistry.BRIEFING_SCREEN_SKIN = this._mapMediator(BriefingScreenMediator, lib.MainAssets.BriefingScreenSkin);

        SkinKeyRegistry.HUB_SCREEN_SKIN = this._mapMediator(HubScreenMediator, lib.MainAssets.HubScreenSkin);
        SkinKeyRegistry.HUB_KEY_DECISION_LIST_SKIN = this._mapMediator(HubKeyDecisionListMediator);
        SkinKeyRegistry.HUB_KEY_DECISION_LIST_ITEM_SKIN = this._mapMediator(HubKeyDecisionListItemMediator);
        
        SkinKeyRegistry.TEA_CORNER_OPENER = this._mapMediator(TeaCornerOpenerMediator);

        SkinKeyRegistry.INTERNAL_STAKEHOLDERS = this._mapMediator(InternalStakeholdersMediator);

        SkinKeyRegistry.MAP_SKIN = this._mapMediator(MapMediator); // Skin differs per map, see MapFactory.js
        SkinKeyRegistry.MAP_LOCATION = this._mapMediator(MapLocationMediator);
        SkinKeyRegistry.MAP_LOCATION_CHARACTER_MENU = this._mapMediator(MapLocationCharacterMenuMediator);
        SkinKeyRegistry.MAP_NODE_INFORMATION_POPUP_SKIN = this._mapMediator(MapCharacterInformationPopupMediator, lib.MainAssets.MapNodeInformationPopupSkin);

        SkinKeyRegistry.KEY_DECISION_OVERLAY_SCREEN_SKIN = this._mapMediator(KeyDecisionOverlayScreenMediator, lib.MainAssets.KeyDecisionOverlayScreenSkin);
        SkinKeyRegistry.KEY_DECISION_UNDERLAY_SCREEN_SKIN = this._mapMediator(KeyDecisionUnderlayScreenMediator, lib.MainAssets.KeyDecisionUnderlayScreenSkin);
        SkinKeyRegistry.KEY_DECISION_AVAILABLE_SCREEN_SKIN = this._mapMediator(KeyDecisionAvailableScreenMediator, lib.MainAssets.KeyDecisionAvailableScreenSkin);

        SkinKeyRegistry.CONVERSATION_STATEMENT_SCREEN_SKIN = this._mapMediator(ConversationStatementScreenMediator, lib.MainAssets.ConversationStatementScreenSkin);
        SkinKeyRegistry.TRANSITION_SCREEN_SKIN = this._mapMediator(TransitionScreenMediator, lib.MainAssets.TransitionScreenSkin);

        this._addAssetsToConfigurationManifest(
            { id: "maps-config", src: "assets/maps.json" },
            { id: "key-decisions-config", src: "assets/key-decisions.json" },
            { id: "internal-stakeholders-config", src: "assets/internal-stakeholders.json" },
        );

        this._addStartupTask(InitMapsConfigTask, "initMapsConfig", 1);
        this._addStartupTask(InitKeyDecisionsConfigTask, "initKeyDecisionsConfig", 1);
        this._addStartupTask(InitInternalStakeholdersTask, "initInternalStakeholdersTask", 1);
    }
}